import { defineRecipe } from '@plurall/elo-v3';

const PANEL_INLINE_SIZE = '390px';
const PANEL_BLOCK_SIZE = '300px';
const PANEL_BLOCK_SIZE_WITH_COMMENT = '400px';

export const FeedbackFloaterRootRecipe = defineRecipe({
  className: 'feedback-floater',
  base: {
    position: 'fixed',
    insetInlineEnd: 0,
    insetBlockStart: '50%',
    transform: 'translateY(-50%)',
    zIndex: 'modal',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    inlineSize: PANEL_INLINE_SIZE,
    blockSize: PANEL_BLOCK_SIZE,
    pointerEvents: 'none',
    transitionProperty: 'block-size',
    transitionDuration: 'fast',
    '&[data-comment-visible="true"]': {
      blockSize: PANEL_BLOCK_SIZE_WITH_COMMENT,
    },
    smDown: {
      insetInline: 'space.16',
      insetBlockStart: 'auto',
      insetBlockEnd: 'space.16',
      transform: 'none',
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      inlineSize: 'auto',
      blockSize: 'auto',
    },
    _print: { display: 'none' },
  },
});

export const FeedbackFloaterTriggerRecipe = defineRecipe({
  className: 'feedback-floater__trigger',
  base: {
    position: 'absolute',
    right: 0,
    bottom: 'space.12',
    zIndex: '1',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'space.8',
    paddingBlock: 'space.16',
    paddingInline: 'space.8',
    borderWidth: 0,
    borderRadius: 'shape.8',
    backgroundColor: 'background.brand',
    color: 'text.always.light.alpha',
    boxShadow: 'md',
    cursor: 'pointer',
    fontFamily: 'body',
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.bold',
    textTransform: 'uppercase',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    flexShrink: 0,
    boxSizing: 'border-box',
    width: 'size.40',
    minWidth: 'size.40',
    maxWidth: 'size.40',
    pointerEvents: 'auto',
    transitionProperty: 'background-color, right',
    transitionDuration: 'fast',
    transitionTimingFunction: 'ease-out',
    _hover: { backgroundColor: 'background.inverse' },
    _active: { transform: 'rotate(180deg)' },
    _focusVisible: {
      outlineWidth: 'border.width.large',
      outlineStyle: 'solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: 'space.4',
    },
    smDown: {
      position: 'static',
      writingMode: 'horizontal-tb',
      transform: 'none',
      width: 'auto',
      minWidth: 'auto',
      maxWidth: 'none',
      paddingBlock: 'space.8',
      paddingInline: 'space.16',
      _active: { transform: 'none' },
    },
    '&[data-open="true"]': {
      right: '100%',
      backgroundColor: 'background.inverse',
      borderTopRightRadius: 'shape.0',
      borderBottomRightRadius: 'shape.0',
      _hover: { backgroundColor: 'background.inverse' },
      smDown: {
        borderRadius: 'shape.8',
      },
    },
  },
});

export const FeedbackFloaterPanelRecipe = defineRecipe({
  className: 'feedback-floater__panel',
  base: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    pointerEvents: 'auto',
    inlineSize: PANEL_INLINE_SIZE,
    blockSize: PANEL_BLOCK_SIZE,
    transitionProperty: 'block-size',
    transitionDuration: 'fast',
    '&[data-comment-visible="true"]': {
      blockSize: PANEL_BLOCK_SIZE_WITH_COMMENT,
    },
    maxInlineSize: 'calc(100vw - {spacing.space.32})',
    maxBlockSize: 'calc(100svh - {spacing.space.32})',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    scrollbarGutter: 'stable',
    padding: 'space.24',
    borderWidth: 0,
    borderRadius: 'shape.8',
    backgroundColor: 'background.primary',
    color: 'text.primary',
    boxShadow: 'lg',
    textAlign: 'center',
    animationDuration: 'fast',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both',
    '&[data-open="true"]': {
      animationName: 'slide-from-right-full',
    },
    '&[data-open="false"]': {
      animationName: 'slide-to-right-full',
      pointerEvents: 'none',
    },
  },
});

export const FeedbackFloaterTitleRecipe = defineRecipe({
  className: 'feedback-floater__title',
  base: {
    margin: 0,
    fontFamily: 'heading',
    fontSize: 'font.size.medium',
    fontWeight: 'font.weight.bold',
    lineHeight: 'font.line.height.moderate',
    color: 'text.primary',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
});

export const FeedbackFloaterFormRecipe = defineRecipe({
  className: 'feedback-floater__form',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.24',
    inlineSize: 'full',
  },
});

export const FeedbackFloaterScoresRecipe = defineRecipe({
  className: 'feedback-floater__scores',
  base: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 'space.16',
    margin: 0,
    '&[data-has-selection="true"] [data-part="item"]': {
      color: 'foreground.tertiary',
    },
    '&[data-has-selection="true"] [data-part="item"][data-state="checked"][data-score="1"]': {
      color: 'foreground.feedback.error.secondary',
    },
    '&[data-has-selection="true"] [data-part="item"][data-state="checked"][data-score="2"]': {
      color: 'foreground.feedback.warning.secondary',
    },
    '&[data-has-selection="true"] [data-part="item"][data-state="checked"][data-score="3"]': {
      color: 'foreground.feedback.new.secondary',
    },
    '&[data-has-selection="true"] [data-part="item"][data-state="checked"][data-score="4"]': {
      color: 'foreground.feedback.success.secondary',
    },
    '&[data-has-selection="true"] [data-part="item"][data-state="checked"][data-score="5"]': {
      color: 'foreground.feedback.information.secondary',
    },
    '&:hover [data-part="item"][data-score]:not(:hover):not([data-state="checked"])': {
      color: 'foreground.tertiary',
    },
    '&:hover [data-part="item"]:hover .feedback-floater__score-label': {
      opacity: 1,
    },
    '&:hover [data-part="item"][data-score="1"]:hover': {
      color: 'foreground.feedback.error.secondary',
    },
    '&:hover [data-part="item"][data-score="2"]:hover': {
      color: 'foreground.feedback.warning.secondary',
    },
    '&:hover [data-part="item"][data-score="3"]:hover': {
      color: 'foreground.feedback.new.secondary',
    },
    '&:hover [data-part="item"][data-score="4"]:hover': {
      color: 'foreground.feedback.success.secondary',
    },
    '&:hover [data-part="item"][data-score="5"]:hover': {
      color: 'foreground.feedback.information.secondary',
    },
  },
});

export const FeedbackFloaterScoreRecipe = defineRecipe({
  className: 'feedback-floater__score',
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'space.4',
    borderRadius: 'shape.999',
    color: 'foreground.tertiary',
    cursor: 'pointer',
    outline: 'none',
    '&[data-score="1"]': {
      color: 'foreground.feedback.error.secondary',
    },
    '&[data-score="2"]': {
      color: 'foreground.feedback.warning.secondary',
    },
    '&[data-score="3"]': {
      color: 'foreground.feedback.new.secondary',
    },
    '&[data-score="4"]': {
      color: 'foreground.feedback.success.secondary',
    },
    '&[data-score="5"]': {
      color: 'foreground.feedback.information.secondary',
    },
    _focusVisible: {
      outlineWidth: 'border.width.large',
      outlineStyle: 'solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: 'space.4',
    },
    _disabled: {
      cursor: 'not-allowed',
      color: 'foreground.disabled',
    },
  },
});

export const FeedbackFloaterScoreIconRecipe = defineRecipe({
  className: 'feedback-floater__score-icon',
  base: {
    inlineSize: 'size.40',
    blockSize: 'size.40',
    color: 'currentColor',
  },
});

export const FeedbackFloaterScoreLabelRecipe = defineRecipe({
  className: 'feedback-floater__score-label',
  base: {
    minBlockSize: 'size.20',
    color: 'currentColor',
    fontSize: 'font.size.small',
    lineHeight: 'font.line.height.short',
    opacity: 0,
    '[data-state="checked"] &': { opacity: 1 },
  },
});

export const FeedbackFloaterFieldRecipe = defineRecipe({
  className: 'feedback-floater__field',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.8',
  },
});

export const FeedbackFloaterLabelRecipe = defineRecipe({
  className: 'feedback-floater__label',
  base: {
    color: 'text.primary',
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.semi.bold',
  },
});

export const FeedbackFloaterTextareaRecipe = defineRecipe({
  className: 'feedback-floater__textarea',
  base: {
    inlineSize: 'full',
    height: 'size.64',
    minHeight: 'size.64',
    resize: 'none',
    padding: 'space.12',
    borderWidth: 'border.width.small',
    borderStyle: 'solid',
    borderColor: 'border.primary',
    borderRadius: 'shape.8',
    backgroundColor: 'background.primary',
    color: 'text.primary',
    fontFamily: 'body',
    fontSize: 'font.size.medium',
    _placeholder: { color: 'text.tertiary' },
    _hover: { borderColor: 'border.brand.primary' },
    _focusVisible: {
      outlineWidth: 'border.width.large',
      outlineStyle: 'solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: 'space.4',
    },
    _disabled: {
      backgroundColor: 'background.disabled',
      color: 'text.disabled',
      cursor: 'not-allowed',
    },
  },
});

export const FeedbackFloaterHelperRecipe = defineRecipe({
  className: 'feedback-floater__helper',
  base: {
    margin: 0,
    color: 'text.secondary',
    fontSize: 'font.size.xsmall',
    lineHeight: 'font.line.height.moderate',
  },
});

export const FeedbackFloaterCounterRecipe = defineRecipe({
  className: 'feedback-floater__counter',
  base: {
    margin: 0,
    color: 'text.tertiary',
    fontSize: 'font.size.xsmall',
    textAlign: 'end',
  },
});

export const FeedbackFloaterActionsRecipe = defineRecipe({
  className: 'feedback-floater__actions',
  base: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 'space.8',
  },
});

export const FeedbackFloaterLoadingRecipe = defineRecipe({
  className: 'feedback-floater__loading',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'space.8',
  },
});

export const FeedbackFloaterSuccessRecipe = defineRecipe({
  className: 'feedback-floater__success',
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'space.24',
    textAlign: 'center',
  },
});

export const FeedbackFloaterTextRecipe = defineRecipe({
  className: 'feedback-floater__text',
  base: {
    margin: 0,
    color: 'text.secondary',
    fontSize: 'font.size.medium',
    lineHeight: 'font.line.height.moderate',
    textAlign: 'center',
  },
});
